import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CustomToastComponent } from '../../shared/components/toast/custom-toast.component';
import { NetworkService } from '../services/network.service';

export const networkInterceptor: HttpInterceptorFn = (
    request: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const networkService = inject(NetworkService);
    const snackBar = inject(MatSnackBar);

    // Skip for certain requests (static assets, etc.)
    if (shouldSkipInterceptor(request)) {
        return next(request);
    }

    // If offline, return queued response
    if (!networkService.isOnline) {
        return handleOfflineRequest(request, networkService);
    }

    // Process request with network error handling
    return next(request).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status === 429) {
                snackBar.openFromComponent(CustomToastComponent, {
                    data: { message: 'Too many requests. Please wait a moment and try again.' },
                    duration: 4000,
                    horizontalPosition: 'right',
                    verticalPosition: 'top',
                    panelClass: ['no-padding-snackbar', 'error-toast'],
                });
                return throwError(() => error);
            }
            if (isNetworkError(error)) {
                return handleNetworkError(request, error, networkService);
            }
            return throwError(() => error);
        })
    );
};

function shouldSkipInterceptor(request: HttpRequest<any>): boolean {
    const skipUrls = ['assets/', 'i18n/', '.json', '.svg', '.png', '.jpg', '.css', '.js'];
    return skipUrls.some(url => request.url.includes(url));
}

function isNetworkError(error: HttpErrorResponse): boolean {
    return error.status === 0 ||          // Network error
        error.status === 408 ||        // Timeout
        error.status === 502 ||        // Bad Gateway
        error.status === 503 ||        // Service Unavailable
        error.status === 504;          // Gateway Timeout
}

function handleOfflineRequest(
    request: HttpRequest<any>,
    networkService: NetworkService
): Observable<HttpEvent<any>> {
    // Queue the request silently
    queueRequest(request);

    // Return queued response that looks like success
    return of(
        new HttpResponse({
            status: 200,
            body: getQueuedResponseBody(request.url)
        })
    );
}

function handleNetworkError(
    request: HttpRequest<any>,
    error: HttpErrorResponse,
    networkService: NetworkService
): Observable<HttpEvent<any>> {
    // Queue for silent retry
    queueRequest(request);

    return of(
        new HttpResponse({
            status: 200,
            body: getQueuedResponseBody(request.url)
        })
    );
}

function queueRequest(request: HttpRequest<any>): void {
    try {
        const queueKey = 'offline_requests_queue';
        const existing = localStorage.getItem(queueKey);
        const queue = existing ? JSON.parse(existing) : [];

        const queuedRequest = {
            id: generateRequestId(),
            url: request.url,
            method: request.method,
            body: request.body,
            headers: request.headers.keys().reduce((acc, key) => {
                acc[key] = request.headers.get(key);
                return acc;
            }, {} as any),
            timestamp: Date.now()
        };

        // Check for duplicates
        const isDuplicate = queue.some((req: any) =>
            req.url === queuedRequest.url &&
            req.method === queuedRequest.method &&
            JSON.stringify(req.body) === JSON.stringify(queuedRequest.body)
        );

        if (!isDuplicate) {
            queue.push(queuedRequest);
            localStorage.setItem(queueKey, JSON.stringify(queue));
        }
    } catch (e) {
        console.warn('Failed to queue request:', e);
    }
}

function getQueuedResponseBody(url: string): any {
    // Return appropriate empty response structure
    const isListRequest =
        url.includes('dashboard') ||
        url.includes('list') ||
        url.includes('getAll') ||
        url.includes('studentdashboard') ||
        url.includes('upcoming') ||
        url.match(/\/api\/.*\?.*=/); // Most API calls with query params

    if (isListRequest) {
        return {
            success: true,
            message: 'Data will load when online',
            data: [],
            queued: true,
            timestamp: Date.now()
        };
    }

    // For detail requests
    return {
        success: true,
        message: 'Data will load when online',
        data: null,
        queued: true,
        timestamp: Date.now()
    };
}

function generateRequestId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
}